import { useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button, Card, CardContent, Typography, Box, Tab, Tabs } from "@mui/material";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useAuth } from "../../contexts/AuthContext";
import {
  useGetMyGroupsQuery,
  useLeaveMyGroupMutation,
} from "../../api/groupsApi";
import { gradientFor } from "../../lib/groupColorPalette";
import LoadingView from "../../components/ui/LoadingView";
import LeaveGroupConfirm from "./components/LeaveGroupConfirm";
import { useToast } from "../../components/ui/Toast";
import GroupRankingView from "./components/insights/GroupRankingView";
import GroupHighlightsView from "./components/insights/GroupHighlightsView";
import GroupTitlesView from "./components/insights/GroupTitlesView";

// Gruplarım sekmeleri (mobildeki sırayla): İstatistik, Olaylar, Ünvanlar.
const MEMBER_TABS = ["İstatistik", "Olaylar", "Ünvanlar"];

const TR_MONTHS = [
  "Ocak","Şubat","Mart","Nisan","Mayıs","Haziran",
  "Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık",
];

function formatTrDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.getDate()} ${TR_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function isManager(group, currentUserId) {
  if (!Array.isArray(group?.managerIds)) return false;
  return group.managerIds.some((m) => {
    const id = typeof m === "object" ? (m.user?._id || m.user) : m;
    return id && id.toString() === String(currentUserId);
  });
}

export default function GroupDetailPage() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { t } = useScriptMode();
  const toast = useToast();
  const { user } = useAuth();
  const currentUserId = user?._id;

  const { data, isLoading } = useGetMyGroupsQuery();
  const group = useMemo(() => {
    const groups = data?.data?.groups || [];
    return groups.find((g) => String(g.id) === String(groupId));
  }, [data, groupId]);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [insightTab, setInsightTab] = useState(0);
  const [leaveGroup, { isLoading: leaveLoading }] = useLeaveMyGroupMutation();

  if (isLoading) return <LoadingView label={t("loading")} />;

  if (!group) {
    return (
      <div className="max-w-3xl mx-auto py-16 flex flex-col items-center gap-4">
        <Typography variant="h6">{t("group_not_found")}</Typography>
        <Button component={Link} to="/uygulama/gruplar" variant="outlined">
          {t("group_back_to_list")}
        </Button>
      </div>
    );
  }

  const manager = isManager(group, currentUserId);
  const banner = gradientFor(group.groupName);

  const handleLeave = async () => {
    try {
      await leaveGroup({ groupId: group.id }).unwrap();
      toast.success(t("group_leave_success"));
      setConfirmOpen(false);
      setTimeout(() => navigate("/uygulama/gruplar"), 250);
    } catch (err) {
      const msg = err?.message || err?.data?.message || t("group_leave_error");
      toast.error(msg);
      setConfirmOpen(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="max-w-3xl mx-auto flex flex-col gap-5 px-4 md:px-0"
    >
      <Link to="/uygulama/gruplar" className="text-terakkiPrimary text-sm">
        {t("group_back_to_list")}
      </Link>

      <Card sx={{ overflow: "hidden", borderRadius: 3 }}>
        <Box sx={{ height: 80, background: banner }} />
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#2E7D62" }}>
              {group.groupName}
            </Typography>
            <span
              className={
                manager
                  ? "text-xs font-Bold px-2 py-1 rounded-full text-white"
                  : "text-xs font-SemiBold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700"
              }
              style={
                manager
                  ? { background: "linear-gradient(120deg,#E1BC59,#B58B23)" }
                  : undefined
              }
            >
              {manager ? t("groups_role_manager") : t("groups_role_member")}
            </span>
          </Box>
          <Typography variant="body2" color="text.secondary" mt={1}>
            {group.memberCount} {t("groups_member_count")} · {group.totalManagers}{" "}
            {t("groups_manager_count")} · {formatTrDate(group.createdAt)}
          </Typography>
        </CardContent>
      </Card>

      {/* Grubun sıralaması, olayları ve ünvanları; her sekme kendi içinde kayar. */}
      <Card sx={{ overflow: "hidden", borderRadius: 3 }}>
        <div className="border-b border-terakkiBorder dark:border-white/15 px-2">
          <Tabs
            value={insightTab}
            onChange={(_, v) => setInsightTab(v)}
            variant="fullWidth"
          >
            {MEMBER_TABS.map((label) => (
              <Tab
                key={label}
                label={t(label)}
                sx={{ minHeight: 48, textTransform: "none", fontSize: 15, fontWeight: 500 }}
              />
            ))}
          </Tabs>
        </div>
        <div className="h-[560px] flex flex-col">
          {insightTab === 0 ? (
            <GroupRankingView groupId={String(group.id)} />
          ) : insightTab === 1 ? (
            <GroupHighlightsView groupId={String(group.id)} />
          ) : (
            <GroupTitlesView groupId={String(group.id)} />
          )}
        </div>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t("group_description_title")}
          </Typography>
          <Typography variant="body2" mt={1}>
            {group.description || t("group_no_description")}
          </Typography>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Card>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t("group_quick_info_title")}
            </Typography>
            <Box mt={2} display="flex" flexDirection="column" gap={1}>
              <div className="flex justify-between">
                <span className="text-terakkiNeutral">{t("group_quick_info_member_label")}</span>
                <span className="font-SemiBold">{group.memberCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-terakkiNeutral">{t("group_quick_info_manager_label")}</span>
                <span className="font-SemiBold">{group.totalManagers}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-terakkiNeutral">{t("group_quick_info_created_label")}</span>
                <span className="font-SemiBold">{formatTrDate(group.createdAt)}</span>
              </div>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t("group_managers_section_title")}
            </Typography>
            <Box
              mt={2}
              p={1.5}
              sx={{ backgroundColor: "rgba(0,0,0,0.04)", borderRadius: 2 }}
            >
              <Typography variant="caption" color="text.secondary">
                {t("group_coming_soon")}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t("group_hatim_section_title")}
          </Typography>
          <Typography variant="body2" mt={1}>
            {t("group_hatim_section_body")}
          </Typography>
        </CardContent>
      </Card>

      <Box display="flex" justifyContent="flex-end">
        <Button
          color="error"
          variant="outlined"
          onClick={() => setConfirmOpen(true)}
        >
          ⚠ {t("group_leave_cta")}
        </Button>
      </Box>

      <LeaveGroupConfirm
        open={confirmOpen}
        loading={leaveLoading}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleLeave}
      />
    </motion.div>
  );
}
